import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import type {
  CoacheePostFeedbackRequestBody,
  CoacheePostFeedbackResponseBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import { query } from '@warp-drive/utilities/json-api';

import { jsonApiOptions, page } from '../../json-api.ts';

/**
 * Builds the REST request to submit coach feedback for a concluded partnership.
 *
 * `coachingFeedbacks` is read-only over JSON:API — writes go through this route,
 * not `createRecord`, so the ll-api security filter and the coaching write
 * routes stay the only paths that can create one.
 */
export async function buildCoacheeGiveFeedbackRequest(
  apis: Apis,
  session: SessionService,
  coachingPartnershipId: string,
  body: CoacheePostFeedbackRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coachee/coaching-partnerships/${encodeURIComponent(coachingPartnershipId)}/feedback`,
    init
  );
}

/**
 * ll-api already holds feedback for this partnership (409). The coachee's
 * answer is on record, so callers treat this as done rather than as a failure.
 */
export class FeedbackAlreadySubmittedError extends Error {
  constructor() {
    super('Feedback has already been submitted for this partnership');
    this.name = 'FeedbackAlreadySubmittedError';
  }
}

export async function parseCoacheeGiveFeedbackResponse(
  response: Response
): Promise<CoacheePostFeedbackResponseBody> {
  if (response.status === 409) {
    throw new FeedbackAlreadySubmittedError();
  }

  if (!response.ok) {
    throw new Error('Failed to submit feedback');
  }

  return (await response.json()) as CoacheePostFeedbackResponseBody;
}

/**
 * Queries `coachingFeedbacks` for a partnership, over the coachee's own
 * `read-my:coachingfeedbacks` scope — a coachee can only ever see their own
 * submission, never a coach's. Used to check whether feedback has already been
 * given before showing the ask again.
 */
export function queryCoachingFeedbackByPartnership(
  coachingPartnershipId: string
) {
  return query(
    DataTypeCoaching.CoachingFeedback,
    {
      'filter[coachingPartnership.id]': coachingPartnershipId,
      ...page({ offset: 0, limit: 1 }),
    },
    jsonApiOptions
  );
}
